import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AGENCY_FIELD_KIND_DEFS,
  AGENCY_FIELD_KIND_MAP,
  MAX_TEMPLATE_FIELDS,
  MAX_TEMPLATE_SECTIONS,
  MAX_TEMPLATE_TASKS,
  PROJECT_COLORS,
  type AgencyFieldKind,
  type AgencyFieldOption,
  type AgencyTemplateDetail,
  type AgencyTemplateView,
  type PortfolioView,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { WORDS, plural } from '../../lib/format';
import { Icon, type IconName } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, Modal, Notice, PageHead } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';

/**
 * قوالب المشاريع.
 *
 * جوابُ سؤالٍ متكرّر: «لماذا حقلٌ أضفتُه في مشروعٍ لا أراه في غيره؟» —
 * لأن الحقول كانت تُعرَّف داخل كلّ مشروعٍ على حدة. فصار التعريفُ هنا مرّةً:
 * أقسامُ اللوح، وحقولُ التفاصيل، والمهامُّ التي تتكرّر في كلّ مشروعٍ من
 * نوعه. وكلُّ مشروعٍ يُنشأ — من عقدٍ أو مباشرةً — يُختار له قالبُه.
 *
 * والقالبُ يُنسخ ولا يُشار إليه: تعديلُه لا يغيّر مشروعًا جاريًا فيه
 * بياناتٌ مكتوبة. ومن أراد نشرَ حقلٍ جديدٍ على القائم ضغط «انشر على
 * المشاريع» — فيُقال له كم مشروعًا مسّ.
 */

/** أيقوناتٌ يختار منها القالب — مغلقةٌ فتبقى علامةً لا زينة. */
const TEMPLATE_ICONS: IconName[] = [
  'briefcase',
  'send',
  'building',
  'video',
  'gantt',
  'layers',
  'chart',
  'star',
  'rocket',
  'target',
  'folder',
  'clipboard',
];

export function AgencyTemplates() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { atLeast } = useAuth();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const templates = useQuery({
    queryKey: ['agency', 'templates', showArchived],
    queryFn: () =>
      api<AgencyTemplateView[]>(`/agency/templates${showArchived ? '?archived=true' : ''}`),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['agency'] });

  const seed = useMutation({
    mutationFn: () => api<{ created: number }>('/agency/templates/starters', { method: 'POST' }),
    onSuccess: (r) => {
      refresh();
      toast(`أُنشئ ${plural(r.created, WORDS.template)} — عدّلها كما تشاء`);
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإنشاء.'),
  });

  const flags = useMutation({
    mutationFn: ({ id, ...body }: { id: string; isDefault?: boolean; archived?: boolean }) =>
      api(`/agency/templates/${id}/flags`, { method: 'POST', body }),
    onSuccess: refresh,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/agency/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      refresh();
      toast('حُذف القالب — ومشاريعه باقيةٌ كما هي');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  const apply = useMutation({
    mutationFn: (id: string) =>
      api<{ projects: number; fields: number; sections: number }>(
        `/agency/templates/${id}/apply`,
        { method: 'POST', body: { withSections: true } },
      ),
    onSuccess: (r) => {
      refresh();
      toast(
        r.projects === 0
          ? 'لا مشاريع لهذا القالب بعد'
          : `أُضيف ${plural(r.fields, WORDS.field)} و${plural(r.sections, WORDS.section)} في ${plural(r.projects, WORDS.project)}`,
      );
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر النشر.'),
  });

  const rows = templates.data ?? [];
  const canEdit = atLeast('MANAGER');

  return (
    <div className="page">
      <PageHead
        title="قوالب المشاريع"
        subtitle="ما يجب أن يوجد في كل مشروعٍ من نوعه — أقسامُه وحقولُه ومهامُّه الجاهزة، معرَّفةً مرّةً"
        actions={
          <>
            <label className="row tiny mute" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              إظهار المؤرشفة
            </label>
            {canEdit && (
              <button className="btn btn-primary" onClick={() => setEditing('new')}>
                <Icon name="plus" size={15} />
                قالب جديد
              </button>
            )}
          </>
        }
      />

      {templates.isError && (
        <ErrorState error={templates.error} onRetry={() => void templates.refetch()} />
      )}
      {templates.isPending && <div className="skeleton" style={{ height: 180 }} />}

      {templates.data && rows.length === 0 && (
        <EmptyState
          title="لا قوالب بعد"
          text="القالبُ يُعرِّف أقسامَ اللوح وحقولَ التفاصيل والمهامَّ المتكرّرة مرّةً واحدة، فيرثها كلُّ مشروعٍ يُنشأ منه. ابدأ بقوالبَ جاهزةٍ ثم عدّلها، أو ابنِ قالبك من الصفر."
          action={
            canEdit ? (
              <div className="row" style={{ gap: 'var(--sp-2)' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => seed.mutate()}
                  disabled={seed.isPending}
                >
                  <Icon name="zap" size={15} />
                  أنشئ قوالبَ جاهزة
                </button>
                <button className="btn" onClick={() => setEditing('new')}>
                  قالبٌ من الصفر
                </button>
              </div>
            ) : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <>
          <Notice kind="info" icon="info" title="القالبُ نسخةٌ لا رابط">
            المشروعُ يأخذ نسخةً من قالبه لحظةَ إنشائه ثمّ ينفصل — فتعديلُ القالب لا يعبث
            بمشروعٍ جارٍ فيه بياناتٌ مكتوبة. ولنشر حقلٍ جديدٍ على مشاريع القالب القائمة اضغط
            «انشر على مشاريعه»: يُضيف الناقصَ ولا يحذف شيئًا.
          </Notice>

          <div className="tpl-grid">
            {rows.map((t) => (
              <article key={t.id} className={`tpl-card${t.archived ? ' is-archived' : ''}`}>
                <span className="tpl-bar" style={{ background: t.color }} />
                <div className="tpl-head">
                  <span className="tpl-icon" style={{ background: `${t.color}1a`, color: t.color }}>
                    <Icon name={t.icon as IconName} size={18} />
                  </span>
                  <div className="tpl-who">
                    <strong className="tpl-name">{t.name}</strong>
                    <span className="tiny mute">
                      {t.portfolioName ? `محفظة ${t.portfolioName}` : 'بلا محفظة'}
                    </span>
                  </div>
                  {t.isDefault && <Badge kind="success">افتراضي</Badge>}
                  {t.archived && <Badge kind="neutral">مؤرشف</Badge>}
                </div>

                {t.description && <p className="tiny mute ag-clamp">{t.description}</p>}

                {/* الأعدادُ بصيغ الجمع العربية — «3 مهمّة» يلحظها القارئ فورًا. */}
                <div className="tpl-counts">
                  <span className="tpl-count">
                    <Icon name="layers" size={12} />
                    {plural(t.sectionCount, WORDS.section)}
                  </span>
                  <span className="tpl-count">
                    <Icon name="clipboard" size={12} />
                    {plural(t.fieldCount, WORDS.field)}
                  </span>
                  <span className="tpl-count">
                    <Icon name="check" size={12} />
                    {plural(t.taskCount, WORDS.task)}
                  </span>
                  <span className="tpl-count">
                    <Icon name="briefcase" size={12} />
                    {plural(t.projectCount, WORDS.project)}
                  </span>
                </div>

                {canEdit && (
                  <div className="tpl-actions">
                    <button className="btn btn-sm" onClick={() => setEditing(t.id)}>
                      <Icon name="edit" size={13} />
                      تعديل
                    </button>
                    {!t.isDefault && !t.archived && (
                      <button
                        className="btn btn-sm"
                        onClick={() => flags.mutate({ id: t.id, isDefault: true })}
                        title="يُنسخ منه كلُّ مشروعٍ يُنشأ بلا اختيار"
                      >
                        <Icon name="star" size={13} />
                        اجعله الافتراضي
                      </button>
                    )}
                    {t.projectCount > 0 && (
                      <button
                        className="btn btn-sm"
                        onClick={() => {
                          if (
                            window.confirm(
                              `نشرُ حقول «${t.name}» وأقسامِه على ${plural(t.projectCount, WORDS.project)}؟ يُضاف الناقصُ ولا يُحذف شيء.`,
                            )
                          ) {
                            apply.mutate(t.id);
                          }
                        }}
                        disabled={apply.isPending}
                        title="أضِف حقولَ القالب الناقصة إلى مشاريعه القائمة"
                      >
                        <Icon name="send" size={13} />
                        انشر على مشاريعه
                      </button>
                    )}
                    <button
                      className="btn btn-sm"
                      onClick={() => flags.mutate({ id: t.id, archived: !t.archived })}
                    >
                      <Icon name={t.archived ? 'refresh' : 'inbox'} size={13} />
                      {t.archived ? 'إعادة' : 'أرشفة'}
                    </button>
                    <button
                      className="btn btn-sm btn-icon"
                      onClick={() => {
                        if (
                          window.confirm(
                            `حذف قالب «${t.name}»؟ المشاريع المنسوخة منه تبقى كما هي.`,
                          )
                        ) {
                          remove.mutate(t.id);
                        }
                      }}
                      aria-label="حذف القالب"
                    >
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        </>
      )}

      {editing && (
        <TemplateEditor
          templateId={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   محرّر القالب — وثيقةٌ تُحرَّر وتُحفظ جملةً
   ========================================================================== */

interface DraftField {
  name: string;
  kind: AgencyFieldKind;
  options: AgencyFieldOption[];
}
interface DraftTask {
  title: string;
  notes: string | null;
  offsetDays: number;
  isMilestone: boolean;
  sectionOrder: number;
}

function TemplateEditor({
  templateId,
  onClose,
  onSaved,
}: {
  templateId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [color, setColor] = useState<string>(PROJECT_COLORS[0]);
  const [icon, setIcon] = useState<IconName>('briefcase');
  const [isDefault, setIsDefault] = useState(false);
  const [portfolioId, setPortfolioId] = useState<string>('');
  const [sections, setSections] = useState<string[]>([...['قائمة المهام', 'قيد التنفيذ', 'مراجعة', 'تسليم']]);
  const [fields, setFields] = useState<DraftField[]>([]);
  const [tasks, setTasks] = useState<DraftTask[]>([]);
  const [loaded, setLoaded] = useState(templateId == null);

  const portfolios = useQuery({
    queryKey: ['agency', 'portfolios'],
    queryFn: () => api<PortfolioView[]>('/agency/portfolios'),
  });

  useQuery({
    queryKey: ['agency', 'template', templateId],
    enabled: templateId != null,
    queryFn: async () => {
      const d = await api<AgencyTemplateDetail>(`/agency/templates/${templateId}`);
      setName(d.name);
      setDescription(d.description ?? '');
      setProjectDescription(d.projectDescription ?? '');
      setColor(d.color);
      setIcon(d.icon as IconName);
      setIsDefault(d.isDefault);
      setPortfolioId(d.portfolioId ?? '');
      setSections(d.sections.map((s) => s.name));
      setFields(d.fields.map((f) => ({ name: f.name, kind: f.kind, options: f.options })));
      setTasks(
        d.tasks.map((t) => ({
          title: t.title,
          notes: t.notes,
          offsetDays: t.offsetDays,
          isMilestone: t.isMilestone,
          sectionOrder: t.sectionOrder,
        })),
      );
      setLoaded(true);
      return d;
    },
  });

  const save = useMutation({
    mutationFn: () =>
      api(templateId ? `/agency/templates/${templateId}` : '/agency/templates', {
        method: templateId ? 'PATCH' : 'POST',
        body: {
          name: name.trim(),
          description: description.trim() || null,
          projectDescription: projectDescription.trim() || null,
          color,
          icon,
          isDefault,
          portfolioId: portfolioId || null,
          sections: sections.filter((s) => s.trim()).map((s) => ({ name: s.trim() })),
          fields: fields
            .filter((f) => f.name.trim())
            .map((f) => ({ name: f.name.trim(), kind: f.kind, options: f.options })),
          tasks: tasks
            .filter((t) => t.title.trim())
            .map((t) => ({
              title: t.title.trim(),
              notes: t.notes,
              offsetDays: t.offsetDays,
              isMilestone: t.isMilestone,
              sectionOrder: Math.min(t.sectionOrder, Math.max(0, sections.length - 1)),
            })),
        },
      }),
    onSuccess: () => {
      toast(templateId ? 'حُفظ القالب' : 'أُنشئ القالب');
      onSaved();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  if (!loaded) {
    return (
      <Modal title="القالب" onClose={onClose} wide>
        <div className="skeleton" style={{ height: 220 }} />
      </Modal>
    );
  }

  return (
    <Modal title={templateId ? `تعديل «${name}»` : 'قالب جديد'} onClose={onClose} wide>
      <div className="tpl-form">
        <section className="fx-sec">
          <h4 className="fx-sec-title">
            <Icon name="clipboard" size={13} /> هويّة القالب
          </h4>
          <div className="grid-2">
            <label className="field">
              <span className="label">اسم القالب</span>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="مثل: عملاء التسويق"
                autoFocus
              />
            </label>
            <label className="field">
              <span className="label">المحفظة (اختياري)</span>
              <select
                className="input"
                value={portfolioId}
                onChange={(e) => setPortfolioId(e.target.value)}
              >
                <option value="">بلا محفظة</option>
                {(portfolios.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="field">
            <span className="label">وصفُ القالب — لمن يختاره</span>
            <input
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="متى يُستعمل هذا القالب؟"
            />
          </label>
          <label className="field">
            <span className="label">وصفُ المشروع الافتراضي — يُنسخ في كل مشروع</span>
            <input
              className="input"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              placeholder="يظهر في خانة وصف المشروع عند إنشائه"
            />
          </label>

          <div className="grid-2">
            <div className="field">
              <span className="label">اللون</span>
              <div className="ag-swatches">
                {PROJECT_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`ag-swatch${color === c ? ' is-active' : ''}`}
                    style={{ background: c }}
                    onClick={() => setColor(c)}
                    aria-label={`اللون ${c}`}
                  />
                ))}
              </div>
            </div>
            <div className="field">
              <span className="label">الأيقونة</span>
              <div className="tpl-icons">
                {TEMPLATE_ICONS.map((ic) => (
                  <button
                    key={ic}
                    type="button"
                    className={`tpl-ic${icon === ic ? ' is-active' : ''}`}
                    onClick={() => setIcon(ic)}
                    aria-label={ic}
                  >
                    <Icon name={ic} size={15} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            القالب الافتراضي — يُنسخ منه كلُّ مشروعٍ يُنشأ بلا اختيار (ومنها مشاريع العقود)
          </label>
        </section>

        {/* الأقسام: أعمدة اللوح */}
        <section className="fx-sec">
          <h4 className="fx-sec-title">
            <Icon name="layers" size={13} /> أقسام اللوح
            <span className="tiny mute"> — أعمدةُ المشروع بترتيبها</span>
          </h4>
          {sections.map((s, i) => (
            <div key={i} className="tpl-row">
              <span className="tpl-idx num">{i + 1}</span>
              <input
                className="input"
                value={s}
                onChange={(e) =>
                  setSections((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
                }
              />
              <button
                className="btn btn-sm btn-icon"
                onClick={() => setSections((prev) => prev.filter((_, j) => j !== i))}
                aria-label="حذف القسم"
              >
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
          <button
            className="btn btn-sm"
            onClick={() => setSections((prev) => [...prev, ''])}
            disabled={sections.length >= MAX_TEMPLATE_SECTIONS}
          >
            <Icon name="plus" size={13} />
            قسم
          </button>
        </section>

        {/* الحقول: تفاصيل المهمة */}
        <section className="fx-sec">
          <h4 className="fx-sec-title">
            <Icon name="clipboard" size={13} /> حقول التفاصيل
            <span className="tiny mute"> — تظهر في كلّ مهمّة داخل المشروع</span>
          </h4>
          {fields.length === 0 && (
            <p className="tiny mute">لا حقول — أضِف ما تحتاج تتبّعه في كلّ مهمّة.</p>
          )}
          {fields.map((f, i) => (
            <div key={i} className="tpl-field">
              <div className="tpl-row">
                <input
                  className="input"
                  value={f.name}
                  placeholder="اسم الحقل"
                  onChange={(e) =>
                    setFields((prev) =>
                      prev.map((v, j) => (j === i ? { ...v, name: e.target.value } : v)),
                    )
                  }
                />
                <select
                  className="input"
                  style={{ maxWidth: '11rem' }}
                  value={f.kind}
                  onChange={(e) =>
                    setFields((prev) =>
                      prev.map((v, j) =>
                        j === i ? { ...v, kind: e.target.value as AgencyFieldKind } : v,
                      ),
                    )
                  }
                >
                  {AGENCY_FIELD_KIND_DEFS.map((k) => (
                    <option key={k.key} value={k.key}>
                      {k.label}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn-sm btn-icon"
                  onClick={() => setFields((prev) => prev.filter((_, j) => j !== i))}
                  aria-label="حذف الحقل"
                >
                  <Icon name="trash" size={13} />
                </button>
              </div>
              {AGENCY_FIELD_KIND_MAP[f.kind].hasOptions && (
                <div className="tpl-opts">
                  {f.options.map((o, oi) => (
                    <span key={o.id} className="tpl-opt">
                      <input
                        className="input input-sm"
                        value={o.label}
                        onChange={(e) =>
                          setFields((prev) =>
                            prev.map((v, j) =>
                              j === i
                                ? {
                                    ...v,
                                    options: v.options.map((oo, ooi) =>
                                      ooi === oi ? { ...oo, label: e.target.value } : oo,
                                    ),
                                  }
                                : v,
                            ),
                          )
                        }
                      />
                      <button
                        className="tpl-opt-x"
                        onClick={() =>
                          setFields((prev) =>
                            prev.map((v, j) =>
                              j === i
                                ? { ...v, options: v.options.filter((_, ooi) => ooi !== oi) }
                                : v,
                            ),
                          )
                        }
                        aria-label="حذف الخيار"
                      >
                        <Icon name="x" size={11} />
                      </button>
                    </span>
                  ))}
                  <button
                    className="btn btn-sm"
                    onClick={() =>
                      setFields((prev) =>
                        prev.map((v, j) =>
                          j === i
                            ? {
                                ...v,
                                options: [
                                  ...v.options,
                                  {
                                    id: `o${Date.now().toString(36)}${v.options.length}`,
                                    label: 'خيار',
                                    color:
                                      PROJECT_COLORS[v.options.length % PROJECT_COLORS.length]!,
                                  },
                                ],
                              }
                            : v,
                        ),
                      )
                    }
                  >
                    <Icon name="plus" size={12} />
                    خيار
                  </button>
                </div>
              )}
            </div>
          ))}
          <button
            className="btn btn-sm"
            onClick={() =>
              setFields((prev) => [...prev, { name: '', kind: 'TEXT', options: [] }])
            }
            disabled={fields.length >= MAX_TEMPLATE_FIELDS}
          >
            <Icon name="plus" size={13} />
            حقل
          </button>
        </section>

        {/* المهام الجاهزة */}
        <section className="fx-sec">
          <h4 className="fx-sec-title">
            <Icon name="check" size={13} /> مهامّ جاهزة
            <span className="tiny mute"> — تُنشأ مع المشروع، وموعدُها يُحسب من يوم إنشائه</span>
          </h4>
          {tasks.length === 0 && (
            <p className="tiny mute">لا مهامّ جاهزة — أضِف الخطوات التي تتكرّر في كلّ مشروع.</p>
          )}
          {tasks.map((t, i) => (
            <div key={i} className="tpl-row">
              <input
                className="input"
                value={t.title}
                placeholder="عنوان المهمّة"
                onChange={(e) =>
                  setTasks((prev) =>
                    prev.map((v, j) => (j === i ? { ...v, title: e.target.value } : v)),
                  )
                }
              />
              <select
                className="input"
                style={{ maxWidth: '10rem' }}
                value={t.sectionOrder}
                onChange={(e) =>
                  setTasks((prev) =>
                    prev.map((v, j) =>
                      j === i ? { ...v, sectionOrder: Number(e.target.value) } : v,
                    ),
                  )
                }
              >
                {sections.map((s, si) => (
                  <option key={si} value={si}>
                    {s || `قسم ${si + 1}`}
                  </option>
                ))}
              </select>
              <span className="in-suffix" style={{ maxWidth: '9rem' }}>
                <input
                  className="input num"
                  type="number"
                  min={0}
                  max={365}
                  value={t.offsetDays}
                  onChange={(e) =>
                    setTasks((prev) =>
                      prev.map((v, j) =>
                        j === i ? { ...v, offsetDays: Number(e.target.value) } : v,
                      ),
                    )
                  }
                />
                <span className="in-suffix-txt">يومًا</span>
              </span>
              <button
                className={`btn btn-sm btn-icon${t.isMilestone ? ' is-on' : ''}`}
                onClick={() =>
                  setTasks((prev) =>
                    prev.map((v, j) => (j === i ? { ...v, isMilestone: !v.isMilestone } : v)),
                  )
                }
                title="معلمٌ رئيسيّ"
                aria-label="معلم رئيسي"
              >
                <Icon name="diamond" size={13} />
              </button>
              <button
                className="btn btn-sm btn-icon"
                onClick={() => setTasks((prev) => prev.filter((_, j) => j !== i))}
                aria-label="حذف المهمّة"
              >
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
          <button
            className="btn btn-sm"
            onClick={() =>
              setTasks((prev) => [
                ...prev,
                { title: '', notes: null, offsetDays: 7, isMilestone: false, sectionOrder: 0 },
              ])
            }
            disabled={tasks.length >= MAX_TEMPLATE_TASKS}
          >
            <Icon name="plus" size={13} />
            مهمّة
          </button>
        </section>
      </div>

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button
          className="btn btn-primary"
          onClick={() => save.mutate()}
          disabled={name.trim().length < 2 || save.isPending}
        >
          {save.isPending ? 'جارٍ الحفظ…' : templateId ? 'حفظ القالب' : 'أنشئ القالب'}
        </button>
        <button className="btn" onClick={onClose}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}
